// pages/Dashboard/Reporte/ResumenTotal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import * as XLSX from "xlsx";
import EmpresaBadge from "../../../components/EmpresaBadge";
import { apiRequest } from "../../../utils/api";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";

Chart.register(...registerables);

const ResumenTotal = () => {
  // Empresa seleccionada
  const { empresaSeleccionada, esConexAdmin } = useEmpresaSelector();

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Datos desde el backend (filtrados por empresa)
  const [cierresCaja, setCierresCaja] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [patrimonios, setPatrimonios] = useState([]);
  const [cuentasPorCobrar, setCuentasPorCobrar] = useState([]);
  const [cuentasPorPagar, setCuentasPorPagar] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [ingresos, setIngresos] = useState([]);

  // Filtros año, mes, día
  const currentYear = new Date().getFullYear();
  const anios = Array.from({ length: 10 }, (_, i) => (2020 + i).toString());
  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  const dias = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, "0"));
  const [anioSeleccionado, setAnioSeleccionado] = useState("");
  const [mesSeleccionado, setMesSeleccionado] = useState("");
  const [diaSeleccionado, setDiaSeleccionado] = useState("");
  const [chartType, setChartType] = useState("line"); // 'line' | 'bar'
  const [groupBy, setGroupBy] = useState("dia"); // 'dia' | 'mes'

  // Cargar datos del backend al cambiar empresa o filtros base (año/mes/día no son necesarios para fetch pero si para recalcular)
  useEffect(() => {
    const cargar = async () => {
      setIsLoading(true);
      try {
        const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
        const qs = empresaId ? `?empresaId=${empresaId}` : '';
        const rCC = await apiRequest(`/cierres-caja${qs}`);
        if (rCC.ok) {
          const data = await rCC.json();
          setCierresCaja(data);
        }
        
        const rEg = await apiRequest(`/egresos${qs}`);
        if (rEg.ok) {
          const data = await rEg.json();
          if (data.length > 0) {
          }
          setEgresos(data);
        }
        
        const rPat = await apiRequest(`/patrimonios${qs}`);
        if (rPat.ok) {
          const data = await rPat.json();
          if (data.length > 0) {
          }
          setPatrimonios(data);
        }
        
        const rCPC = await apiRequest(`/cuentas-por-cobrar${qs}`);
        if (rCPC.ok) {
          const data = await rCPC.json();
          if (data.length > 0) {
          }
          setCuentasPorCobrar(data);
        }
        
        const rCPP = await apiRequest(`/cuentas-por-pagar${qs}`);
        if (rCPP.ok) {
          const data = await rCPP.json();
          if (data.length > 0) {
          }
          setCuentasPorPagar(data);
        }

        const rInv = await apiRequest(`/inventario${qs}`);
        if (rInv.ok) {
          const data = await rInv.json();
          if (data.length > 0) {
          }
          setInventario(data);
        }

        const rIng = await apiRequest(`/ingresos${qs}`);
        if (rIng.ok) {
          const data = await rIng.json();
          if (data.length > 0) {
          }
          setIngresos(data);
        }
      } catch (e) {
        console.error('❌ Error cargando datos para Resumen Total:', e);
        setCierresCaja([]);
        setEgresos([]);
        setPatrimonios([]);
        setCuentasPorCobrar([]);
        setCuentasPorPagar([]);
        setInventario([]);
        setIngresos([]);
      } finally {
        setIsLoading(false);
      }
    };
    cargar();
  }, [empresaSeleccionada?.id, esConexAdmin]);

  // Función auxiliar para comparar fechas ignorando la hora y zona horaria
  const esMismoDia = (fecha1, fecha2) => {
    if (!fecha1 || !fecha2) return false;

    // Extraer solo la parte de fecha (YYYY-MM-DD) para comparación
    const fecha1Str = fecha1.split('T')[0];
    const fecha2Str = fecha2.split('T')[0];

    return fecha1Str === fecha2Str;
  };

  // Función para obtener fecha de un item
  const getFecha = (item) => {
    const fecha = item.fechaFactura || item.fechaVencimiento || item.fecha;
    if (!fecha) return { anio: "", mes: "", dia: "" };

    try {
      const fechaStr = fecha.split('T')[0];
      const [anio, mes, dia] = fechaStr.split('-');

      return {
        anio: anio || "",
        mes: mes || "",
        dia: dia || ""
      };
    } catch (e) {
      console.error('Error al procesar fecha:', e);
      return { anio: "", mes: "", dia: "" };
    }
  };

  // Función para filtrar por año, mes y día
  const filtrar = (datos, tipo) => {
    if (!datos || !Array.isArray(datos)) return [];

    // Para inventario, no aplicar filtros de fecha
    if (tipo === 'inventario') {
      return [...datos]; // Retornar copia del array
    }

    return datos.filter(item => {
      if (!item) return false;

      const fechaItem = item.fecha || item.fechaCreacion || item.createdAt;
      if (!fechaItem) return false;

      const fechaStr = fechaItem.split('T')[0];
      const [anioItem, mesItem, diaItem] = fechaStr.split('-');

      const cumpleAnio = !anioSeleccionado || anioItem === anioSeleccionado;
      const cumpleMes = !mesSeleccionado || mesItem === mesSeleccionado.padStart(2, '0');
      const cumpleDia = !diaSeleccionado || diaItem === diaSeleccionado.padStart(2, '0');

      return cumpleAnio && cumpleMes && cumpleDia;
    });
  };
  
    // Función para agrupar datos por período
  const agruparPorPeriodo = (data, periodo) => {
    const grupos = {};
    data.forEach(item => {
      const { anio, mes, dia } = getFecha(item);
      let key;
      if (periodo === 'dia') {
        key = `${anio}-${mes}-${dia}`;
      } else {
        key = `${anio}-${mes}`;
      }
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(item);
    });
    return grupos;
  };



  const cierresCajaFiltrados = filtrar(cierresCaja, "cierresCaja");
  const egresosFiltrados = filtrar(egresos, "egresos");
  const patrimoniosFiltrados = filtrar(patrimonios, "patrimonios");
  const cuentasPorCobrarFiltradas = filtrar(cuentasPorCobrar, "cuentasPorCobrar");
  const cuentasPorPagarFiltradas = filtrar(cuentasPorPagar, "cuentasPorPagar");
  const inventarioFiltrado = filtrar(inventario, "inventario");
  const ingresosFiltrados = filtrar(ingresos, "ingresos");
  // Calcular totales filtrados
  // 1. Ingreso Diario (Total Pagado de CierreCaja)
  const totalIngresoDiario = cierresCajaFiltrados.reduce((acc, c) => {
    if (c.tipoPago === 'porPagar') {
      return acc + (parseFloat(c.abono) || 0);
    }
    return acc + (parseFloat(c.pagado) || 0);
  }, 0);

  // 2. Cuentas por Cobrar (Total a Cobrar de Ingresos)
  const totalCuentasPorCobrar = isLoading ? 0 : cuentasPorCobrarFiltradas.reduce((acc, c) => acc + (parseFloat(c.saldoCancelado) || 0), 0);

  // 3. Egresos (Total Egresos de Egresos)
  const totalEgresos = isLoading ? 0 : egresosFiltrados.reduce((acc, e) => acc + (parseFloat(e.monto) || 0), 0);

  // 4. Cuentas por Pagar (Total a Pagar de Egresos)
  const totalCuentasPorPagar = isLoading ? 0 : cuentasPorPagarFiltradas.reduce((acc, c) => acc + (parseFloat(c.saldoAdeudado) || 0), 0);

  // 5. Patrimonio (PATRIMONIO NETO de Patrimonio.jsx)
  const totalActivosCorrientes = patrimoniosFiltrados
    .filter(p => p.tipo === 'activo_corriente')
    .reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const totalActivosNoCorrientes = patrimoniosFiltrados
    .filter(p => p.tipo === 'activo_no_corriente')
    .reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const totalPasivosCorrientes = patrimoniosFiltrados
    .filter(p => p.tipo === 'pasivo_corriente')
    .reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const totalPasivosNoCorrientes = patrimoniosFiltrados
    .filter(p => p.tipo === 'pasivo_no_corriente')
    .reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  
  const totalActivos = totalActivosCorrientes + totalActivosNoCorrientes;
  const totalPasivos = totalPasivosCorrientes + totalPasivosNoCorrientes;
  const patrimonioNeto = totalActivos - totalPasivos;

  // Calcular ventas por producto - Solo ventas del día seleccionado, como en Productos.jsx
  const ventasPorProducto = useMemo(() => {
    const mapa = {};
    const fechaSeleccionada = diaSeleccionado ? `${anioSeleccionado}-${mesSeleccionado || '01'}-${diaSeleccionado}` : null;

    // Si hay día seleccionado, filtrar solo ventas de ese día
    if (fechaSeleccionada) {
      ingresos.forEach(ingreso => {
        const fechaIngreso = ingreso.fecha || ingreso.createdAt;
        if (fechaIngreso) {
          const fechaIngresoStr = fechaIngreso.split('T')[0];

          if (fechaIngresoStr === fechaSeleccionada) {
            mapa[ingreso.producto] = (mapa[ingreso.producto] || 0) + (parseFloat(ingreso.cantidad) || 0); // Suma de cantidades vendidas
          }
        }
      });
    } else {
      // Si no hay día seleccionado, no contar ventas (como en Productos.jsx sin filtro de fecha)
      // Esto significa que el inventario mostrará el valor total sin restar ventas
    }
    return mapa;
  }, [ingresos, anioSeleccionado, mesSeleccionado, diaSeleccionado]);

  // Calcular ventas por producto del día actual para totalInventario, como en Productos.jsx
  const ventasPorProductoHoy = useMemo(() => {
    const mapa = {};
    const fechaActual = new Date().toISOString().split('T')[0];
    ingresos.forEach(ingreso => {
      const fechaIngreso = ingreso.fecha || ingreso.createdAt;
      if (fechaIngreso) {
        const fechaIngresoStr = fechaIngreso.split('T')[0];

        if (fechaIngresoStr === fechaActual) {
          mapa[ingreso.producto] = (mapa[ingreso.producto] || 0) + (parseFloat(ingreso.cantidad) || 0);
        }
      }
    });
    return mapa;
  }, [ingresos]);

  // Calcular total inventario - Precio Stock Final Total, como en Productos.jsx
  const totalInventario = useMemo(() => {
    if (isLoading) {
      return 0;
    }

    if (!inventarioFiltrado.length) {
      return 0;
    }
    // Calcular el precio stock final total: precioUnitario * stockFinalDia para cada item, como en Productos.jsx
    const total = inventarioFiltrado.reduce((acc, item) => {
      const vendido = ventasPorProductoHoy[item.nombre] || 0;
      const cantidadEnStock = parseFloat(item.cantidadEnStock) || 0;
      // Usar salida manual si existe, sino usar ventas calculadas
      const salida = item.salida !== undefined ? item.salida : vendido;
      const stockFinalDia = Math.max(cantidadEnStock - salida, 0);
      const costoArticulo = parseFloat(item.costoArticulo) || 0;
      const iva = parseFloat(item.iva) || 0;
      const precioUnitario = costoArticulo * (1 + iva / 100);
      const precioStockFinal = Math.round(precioUnitario * stockFinalDia * 100) / 100; // Redondear a 2 decimales como en Productos.jsx
      return acc + precioStockFinal;
    }, 0);
    return total;
  }, [inventarioFiltrado, ventasPorProductoHoy, isLoading]);
  // Solo mostrar totales cuando no esté cargando

  // Datos para el gráfico agrupados por período
  const cierresCajaGrupos = agruparPorPeriodo(cierresCajaFiltrados, groupBy);
  const cuentasPorCobrarGrupos = agruparPorPeriodo(cuentasPorCobrarFiltradas, groupBy);
  const egresosGrupos = agruparPorPeriodo(egresosFiltrados, groupBy);
  const cuentasPorPagarGrupos = agruparPorPeriodo(cuentasPorPagarFiltradas, groupBy);
  const patrimonioGrupos = agruparPorPeriodo(patrimoniosFiltrados, groupBy);

  // Función para obtener nombre del día de la semana
  const getDiaSemana = (fechaStr) => {
    const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    try {
      const fecha = new Date(fechaStr + (fechaStr.split('-').length === 2 ? '-01' : '')); // Agregar día 01 si es mes
      return diasSemana[fecha.getDay()];
    } catch {
      return fechaStr; // Fallback a la fecha original si hay error
    }
  };

  const allKeys = new Set([
    ...Object.keys(cierresCajaGrupos), 
    ...Object.keys(cuentasPorCobrarGrupos), 
    ...Object.keys(egresosGrupos), 
    ...Object.keys(cuentasPorPagarGrupos),
    ...Object.keys(patrimonioGrupos)
  ]);
  const sortedKeys = Array.from(allKeys).sort();

  // Crear labels con nombres de días cuando es agrupado por día
  const labels = groupBy === 'dia'
    ? sortedKeys.map(key => getDiaSemana(key))
    : sortedKeys;

    // Calculate total inventory value for chart (using all inventory, not filtered)
    const totalInventarioChart = inventario.reduce((acc, item) => {
      const valorFinalDia = item.valorFinalDia || (item.cantidadEnStock || 0) * (item.costoArticulo || 0);
      return acc + (parseFloat(valorFinalDia) || 0);
    }, 0);
// Agregar esto junto con las otras definiciones de datos
const inventarioData = sortedKeys.map(key => {
  const items = inventario.filter(item => {
    const { anio, mes, dia } = getFecha(item);
    if (groupBy === 'dia') {
      return `${anio}-${mes}-${dia}` === key;
    } else {
      return `${anio}-${mes}` === key;
    }
  });
  return items.reduce((acc, item) => {
    const vendido = ventasPorProductoHoy[item.nombre] || 0;
    const cantidadEnStock = parseFloat(item.cantidadEnStock) || 0;
    // Usar salida manual si existe, sino usar ventas calculadas
    const salida = item.salida !== undefined ? item.salida : vendido;
    const stockFinalDia = Math.max(cantidadEnStock - salida, 0);
    const costoArticulo = parseFloat(item.costoArticulo) || 0;
    const iva = parseFloat(item.iva) || 0;
    const precioUnitario = costoArticulo * (1 + iva / 100);
    const precioStockFinal = Math.round(precioUnitario * stockFinalDia * 100) / 100; // Redondear a 2 decimales como en Productos.jsx
    return acc + precioStockFinal;
  }, 0);
});

  // Datos para cada serie del gráfico
  const ingresoDiarioData = sortedKeys.map(key => {
    const cierres = cierresCajaGrupos[key] || [];
    return cierres.reduce((acc, c) => {
      if (c.tipoPago === 'porPagar') {
        return acc + (parseFloat(c.abono) || 0);
      }
      return acc + (parseFloat(c.pagado) || 0);
    }, 0);
  });

  const cuentasPorCobrarData = sortedKeys.map(key =>
    (cuentasPorCobrarGrupos[key] || []).reduce((acc, c) => acc + (parseFloat(c.saldoCancelado) || 0), 0)
  );

  const egresosData = sortedKeys.map(key => 
    (egresosGrupos[key] || []).reduce((acc, e) => acc + (parseFloat(e.monto) || 0), 0)
  );

  const cuentasPorPagarData = sortedKeys.map(key => 
    (cuentasPorPagarGrupos[key] || []).reduce((acc, c) => acc + (parseFloat(c.saldoAdeudado) || 0), 0)
  );

  const patrimonioData = sortedKeys.map(key => {
    const items = patrimonioGrupos[key] || [];
    const activos = items.filter(p => p.tipo === 'activo').reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    const pasivos = items.filter(p => p.tipo === 'pasivo').reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    return activos - pasivos;
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Inventario",
        data: inventarioData,
        borderColor: "#8B4513",
        backgroundColor: "rgba(139, 69, 19, 0.2)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Ingreso Diario",
        data: ingresoDiarioData,
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Cuentas por Cobrar",
        data: cuentasPorCobrarData,
        borderColor: "#2196F3",
        backgroundColor: "rgba(33, 150, 243, 0.2)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Egresos",
        data: egresosData,
        borderColor: "#FF9800",
        backgroundColor: "rgba(255, 152, 0, 0.2)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Cuentas por Pagar",
        data: cuentasPorPagarData,
        borderColor: "#F44336",
        backgroundColor: "rgba(244, 67, 54, 0.2)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Patrimonio Neto",
        data: patrimonioData,
        borderColor: "#9C27B0",
        backgroundColor: "rgba(156, 39, 176, 0.2)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: "#111" } },
    },
    scales: {
      x: { ticks: { color: "#111" } },
      y: { beginAtZero: true, ticks: { color: "#111" } },
    },
  };

  return (
    <div className="p-4">
      <div className="box">
        <h2 className="title is-4 has-text-black">Resumen Total Financiero</h2>
        <EmpresaBadge />
      {/* Filtros año, mes, día */}
      <div className="columns is-vcentered mb-4 is-centered" style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="column is-3">
          <label className="label">Año</label>
          <div className="select is-fullwidth">
            <select value={anioSeleccionado} onChange={(e) => setAnioSeleccionado(e.target.value)}>
              {anios.map(anio => <option key={anio} value={anio}>{anio}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-4">
          <label className="label">Mes</label>
          <div className="select is-fullwidth">
            <select value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)}>
              <option value="">Todos los meses</option>
              {meses.map(mes => <option key={mes} value={mes}>{mes}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-3">
          <label className="label">Día</label>
          <div className="select is-fullwidth">
            <select value={diaSeleccionado} onChange={(e) => setDiaSeleccionado(e.target.value)}>
              <option value="">Todos los días</option>
              {dias.map(dia => <option key={dia} value={dia}>{dia}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="button is-light is-fullwidth" onClick={() => { setAnioSeleccionado(currentYear.toString()); setMesSeleccionado(""); setDiaSeleccionado(""); }}>Reset</button>
        </div>
      </div>
      {/* Acciones */}
      <div className="field is-grouped is-grouped-right mb-3">
        <p className="control">
          <button
            className="button is-small is-light"
            onClick={() => {
              // Construir hoja para Excel con las nuevas categorías
              const rows = [
                ["Categoría", "Total"],
                ["Inventario", totalInventario],
                ["Ingreso Diario", totalIngresoDiario],
                ["Cuentas por Cobrar", totalCuentasPorCobrar],
                ["Egresos", totalEgresos],
                ["Cuentas por Pagar", totalCuentasPorPagar],
                ["Patrimonio Neto", patrimonioNeto],
              ];
              const ws = XLSX.utils.aoa_to_sheet(rows);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "ResumenTotal");
              const nameParts = [anioSeleccionado];
              if (mesSeleccionado) nameParts.push(mesSeleccionado);
              if (diaSeleccionado) nameParts.push(diaSeleccionado);
              XLSX.writeFile(wb, `ResumenFinanciero_${nameParts.join("_") || "Todos"}.xlsx`);
            }}
          >
            Descargar Excel
          </button>
        </p>
        <p className="control">
          <button
            className="button is-small is-primary"
            onClick={async () => {
              // Guardar resumen en backend
              try {
                const resumenData = {
                  inventario: totalInventario,
                  ingresoDiario: totalIngresoDiario,
                  cuentasPorCobrar: totalCuentasPorCobrar,
                  egresos: totalEgresos,
                  cuentasPorPagar: totalCuentasPorPagar,
                  patrimonioNeto: patrimonioNeto,
                  anio: anioSeleccionado,
                  mes: mesSeleccionado,
                  dia: diaSeleccionado,
                  empresaId: empresaSeleccionada?.id || null,
                };

                const response = await apiRequest('/resumen-total', {
                  method: 'POST',
                  body: JSON.stringify(resumenData),
                });

                if (response.ok) {
                  alert('Resumen guardado exitosamente');
                } else {
                  alert('Error al guardar el resumen');
                }
              } catch (error) {
                console.error('Error guardando resumen:', error);
                alert('Error al guardar el resumen');
              }
            }}
          >
            Guardar Resumen
          </button>
        </p>
      </div>

      <table className="table is-striped has-text-black">
        <thead>
          <tr>
            <th className="has-text-black">Categoría</th>
            <th className="has-text-black">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="has-text-black">Inventario</td>
            <td className="has-text-black">${totalInventario.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
          <tr>
            <td className="has-text-black">Ingreso Diario</td>
            <td className="has-text-black">${totalIngresoDiario.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
          <tr>
            <td className="has-text-black">Cuentas por Cobrar</td>
            <td className="has-text-black">${totalCuentasPorCobrar.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
          <tr>
            <td className="has-text-black">Egresos</td>
            <td className="has-text-black">${totalEgresos.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
          <tr>
            <td className="has-text-black">Cuentas por Pagar</td>
            <td className="has-text-black">${totalCuentasPorPagar.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
          <tr>
            <td className="has-text-black">Patrimonio Neto</td>
            <td className="has-text-black">${patrimonioNeto.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ maxWidth: 800, height: 300, margin: "0 auto" }}>
        {chartType === 'line' ? (
          <Line data={data} options={options} />
        ) : (
          <Bar data={{ ...data, datasets: [{ ...data.datasets[0], backgroundColor: ["#4CAF50", "#F44336", "#2196F3"] }] }} options={options} />
        )}
      </div>
    </div>
  </div>
);
};

export default ResumenTotal;
